import { db } from '@/lib/db';
import { isSupabaseConfigured, supabase } from '@/lib/supabase';
import { invalidateCatalogCache } from '@/lib/useCatalog';

export type DeleteResult =
  { status: 'borrado'; remote: boolean } | { status: 'error'; message: string };

/**
 * Borrado completo de la cuenta y sus datos (SPEC 6.E y 10).
 *
 * Aquí SÍ se borra de verdad, no con `deletedAt`: el borrado lógico existe
 * para que una eliminación se propague entre dispositivos, pero cuando alguien
 * ejerce su derecho de supresión lo que espera es que no quede nada.
 *
 * Con sesión, primero el servidor (la función `delete-account` borra el
 * usuario de Auth y, en cascada, todas sus filas) y SOLO si eso va bien, el
 * móvil. Si falla —sin conexión, sesión caducada—, no se borra nada y se dice:
 * antes se borraba lo local y se volvía a la app como si no quedara nada,
 * mientras todo seguía en la nube.
 */
export async function deleteAccount(): Promise<DeleteResult> {
  let remote = false;

  if (isSupabaseConfigured && supabase) {
    // getSession no usa la red: dice si hay datos en la nube que borrar.
    const { data } = await supabase.auth.getSession();
    if (data.session) {
      const failed: DeleteResult = {
        status: 'error',
        message:
          'No se ha podido borrar tu cuenta en el servidor (¿sin conexión?). No se ha borrado nada: vuelve a intentarlo con conexión.',
      };
      try {
        const res = await supabase.functions.invoke<{ ok?: boolean }>('delete-account', {
          body: {},
        });
        if (res.error != null || res.data?.ok !== true) return failed;
      } catch {
        return failed;
      }
      remote = true;
    }
  }

  // El catálogo de alimentos se va con el resto: se vuelve a descargar solo.
  await db.delete();
  invalidateCatalogCache();

  // El usuario ya no existe en el servidor: solo queda olvidar la sesión local.
  if (supabase) await supabase.auth.signOut({ scope: 'local' }).catch(() => undefined);

  try {
    localStorage.clear();
  } catch {
    // Navegación privada: no hay nada que limpiar.
  }

  return { status: 'borrado', remote };
}
